import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { XCircle, Bot, User, MessageSquare, Mail, Folder, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { GraphNode, GraphEdge, NodeType, EdgeType } from '../types/connections'
import { cn } from '../lib/utils'
import ForceGraph2D from 'react-force-graph-2d'

interface ConnectionsGraphProps {
    nodes: GraphNode[]
    edges: GraphEdge[]
    className?: string
}

const getNodeIcon = (label: string, type: NodeType) => {
    const lowerLabel = label.toLowerCase()

    // Check for specific services first
    if (lowerLabel.includes('teams')) return MessageSquare
    if (lowerLabel.includes('email') || lowerLabel.includes('outlook') || lowerLabel.includes('calendar')) return Mail
    if (lowerLabel.includes('sharepoint')) return Folder
    if (lowerLabel.includes('onedrive')) return Folder

    // Then check by type
    if (type === 'assistant' || type === 'agent') return Bot
    if (type === 'human') return User
    if (type === 'source') return Folder

    return Bot // Default
}

const getNodeColor = (type: NodeType, status?: string) => {
    if (status === 'blocked') return '#ef4444' // red-500
    if (status === 'approval') return '#f97316' // orange-500
    if (status === 'pending') return '#eab308' // yellow-500

    switch (type) {
        case 'assistant':
            return 'hsl(var(--primary))'
        case 'source':
            return '#3b82f6' // blue-500
        case 'agent':
            return '#a855f7' // purple-500
        case 'human':
            return '#22c55e' // green-500
        default:
            return 'hsl(var(--muted-foreground))'
    }
}

const getEdgeColor = (type: EdgeType) => {
    switch (type) {
        case 'direct':
            return 'hsl(var(--primary))'
        case 'approval':
            return 'hsl(var(--muted-foreground))'
        case 'blocked':
            return '#ef4444' // red-500
        default:
            return 'hsl(var(--border))'
    }
}

export const ConnectionsGraph: React.FC<ConnectionsGraphProps> = ({
    nodes,
    edges,
    className,
}) => {
    const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
    const [hoveredNode, setHoveredNode] = useState<string | null>(null)
    const fgRef = useRef<any>(null)
    const [zoomLevel, setZoomLevel] = useState(0.5) // Start zoomed out

    // Transform nodes and edges for react-force-graph
    const graphData = useMemo(() => {
        // Separate nodes by type for better layout
        const assistants = nodes.filter(n => n.type === 'assistant')
        const sources = nodes.filter(n => n.type === 'source')
        const agents = nodes.filter(n => n.type === 'agent')

        // Calculate center of canvas (assuming larger viewport for better spread)
        const centerX = 500
        const centerY = 350
        const radius = 300 // Increased distance from center for each group

        // Convert nodes with strategic positioning
        const graphNodes = nodes.map((node) => {
            let initialX = node.x
            let initialY = node.y
            let fixed = false

            // If x and y are provided, use them
            if (initialX && initialY) {
                return {
                    id: node.id,
                    label: node.label,
                    type: node.type,
                    status: node.status,
                    description: node.description,
                    fx: node.type === 'assistant' ? initialX : undefined,
                    fy: node.type === 'assistant' ? initialY : undefined,
                    x: initialX,
                    y: initialY,
                }
            }

            // Position by type - spread out more on all sides
            if (node.type === 'assistant') {
                // Assistants on the left side - spread further
                const assistantIndex = assistants.findIndex(n => n.id === node.id)
                const totalAssistants = assistants.length
                const spacing = totalAssistants > 1 ? 180 : 0
                initialX = centerX - radius - 150
                initialY = centerY + (assistantIndex - (totalAssistants - 1) / 2) * spacing
                fixed = true
            } else if (node.type === 'source') {
                // Sources on the top - spread further
                const sourceIndex = sources.findIndex(n => n.id === node.id)
                const totalSources = sources.length
                const spacing = totalSources > 1 ? 180 : 0
                initialX = centerX + (sourceIndex - (totalSources - 1) / 2) * spacing
                initialY = centerY - radius - 100
            } else if (node.type === 'agent') {
                // Agents on the right side - spread further
                const agentIndex = agents.findIndex(n => n.id === node.id)
                const totalAgents = agents.length
                const spacing = totalAgents > 1 ? 180 : 0
                initialX = centerX + radius + 150
                initialY = centerY + (agentIndex - (totalAgents - 1) / 2) * spacing
            } else {
                // Fallback: distribute in a circle with larger radius
                const allNodes = [...assistants, ...sources, ...agents]
                const index = allNodes.findIndex(n => n.id === node.id)
                const angle = (index / allNodes.length) * 2 * Math.PI
                initialX = centerX + radius * Math.cos(angle)
                initialY = centerY + radius * Math.sin(angle)
            }

            return {
                id: node.id,
                label: node.label,
                type: node.type,
                status: node.status,
                description: node.description,
                fx: fixed ? initialX : undefined,
                fy: fixed ? initialY : undefined,
                x: initialX,
                y: initialY,
            }
        })

        // Convert edges - use node IDs directly
        const graphEdges = edges.map(edge => ({
            id: edge.id,
            source: edge.sourceId,
            target: edge.targetId,
            type: edge.type,
            label: edge.label,
        }))

        return {
            nodes: graphNodes,
            links: graphEdges,
        }
    }, [nodes, edges])

    const handleNodeClick = useCallback((node: any) => {
        const originalNode = nodes.find(n => n.id === node.id)
        if (originalNode) {
            setSelectedNode(originalNode)
        }
    }, [nodes])

    const handleNodeHover = useCallback((node: any) => {
        setHoveredNode(node ? node.id : null)
    }, [])

    // Configure force simulation after mount
    useEffect(() => {
        if (fgRef.current) {
            const fg = fgRef.current
            // Configure forces to prevent jumbling and keep nodes separated
            fg.d3Force('charge')?.strength((node: any) => {
                // Stronger repulsion to keep nodes apart
                return node.type === 'assistant' ? -1500 : -800
            })
            fg.d3Force('link')?.distance((link: any) => {
                // Longer links to spread nodes out more
                if (link.type === 'approval') return 250
                if (link.type === 'blocked') return 200
                return 280 // Increased default distance for better spread
            })
            fg.d3Force('link')?.strength(0.2) // Weaker link force to allow more spread
            fg.d3Force('collision')?.radius((node: any) => {
                // Larger collision radius to prevent overlap
                return node.type === 'assistant' ? 40 : 35
            })
            fg.d3Force('collision')?.strength(1.0) // Strong collision to prevent overlap

            // Add center force to keep nodes from drifting too far (weaker to allow more spread)
            fg.d3Force('center')?.strength(0.05)

            // Add x/y positioning forces to maintain layout by type with updated center
            fg.d3Force('x', (node: any) => {
                if (node.fx !== undefined) return 0 // Don't apply if fixed
                // Gentle force to maintain horizontal position by type
                if (node.type === 'assistant') {
                    return (node.x - (500 - 450)) * 0.05
                } else if (node.type === 'agent') {
                    return (node.x - (500 + 450)) * 0.05
                }
                return 0
            })

            fg.d3Force('y', (node: any) => {
                if (node.fy !== undefined) return 0 // Don't apply if fixed
                // Gentle force to maintain vertical position by type
                if (node.type === 'source') {
                    return (node.y - (350 - 400)) * 0.05
                }
                return 0
            })
        }
    }, [graphData])

    // Set initial zoom when component mounts
    useEffect(() => {
        if (fgRef.current) {
            // Use setTimeout to ensure the graph is fully initialized
            setTimeout(() => {
                fgRef.current.zoom(zoomLevel)
                // Center the view on the updated center coordinates
                fgRef.current.centerAt(500, 350, 1000)
            }, 100)
        }
    }, [zoomLevel])


    const handleZoomIn = useCallback(() => {
        if (fgRef.current) {
            const newZoom = Math.min(zoomLevel + 0.2, 2)
            setZoomLevel(newZoom)
            fgRef.current.zoom(newZoom, 200)
        }
    }, [zoomLevel])

    const handleZoomOut = useCallback(() => {
        if (fgRef.current) {
            const newZoom = Math.max(zoomLevel - 0.2, 0.3)
            setZoomLevel(newZoom)
            fgRef.current.zoom(newZoom, 200)
        }
    }, [zoomLevel])

    const handleResetView = useCallback(() => {
        if (fgRef.current) {
            setZoomLevel(0.5)
            fgRef.current.zoom(0.5, 200)
            fgRef.current.centerAt(500, 350, 1000)
        }
    }, [])

    return (
        <div className={cn("space-y-4", className)}>
            <div className="relative border rounded-lg bg-card overflow-hidden" style={{ width: '100%', height: '600px' }}>
                {/* Zoom Controls */}
                <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-background/80 backdrop-blur-sm"
                        onClick={handleZoomIn}
                        title="Zoom in"
                    >
                        <ZoomIn className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-background/80 backdrop-blur-sm"
                        onClick={handleZoomOut}
                        title="Zoom out"
                    >
                        <ZoomOut className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-background/80 backdrop-blur-sm"
                        onClick={handleResetView}
                        title="Reset view"
                    >
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                </div>
                <ForceGraph2D
                    ref={fgRef}
                    graphData={graphData}
                    nodeLabel={(node: any) => node.label}
                    nodeColor={(node: any) => getNodeColor(node.type, node.status)}
                    nodeVal={(node: any) => {
                        // Size based on type - larger for better visibility
                        if (node.type === 'assistant') return 20
                        if (node.type === 'source') return 15
                        if (node.type === 'agent') return 15
                        return 12
                    }}
                    linkColor={(link: any) => getEdgeColor(link.type)}
                    linkWidth={(link: any) => link.type === 'blocked' ? 1 : 2}
                    linkDirectionalArrowLength={6}
                    linkDirectionalArrowRelPos={1}
                    linkCurvature={0.1}
                    onNodeClick={handleNodeClick}
                    onNodeHover={handleNodeHover}
                    onNodeDragEnd={(node: any) => {
                        // Fix node position after dragging (except central node)
                        if (node.type !== 'assistant') {
                            node.fx = node.x
                            node.fy = node.y
                        }
                    }}
                    onNodeDrag={(node: any) => {
                        // Keep central node fixed at center
                        if (node.type === 'assistant') {
                            node.fx = 500
                            node.fy = 350
                        }
                    }}
                    nodeCanvasObject={(node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
                        const label = node.label
                        const fontSize = 12 / Math.max(globalScale, 1)
                        ctx.font = `${fontSize}px Sans-Serif`
                        ctx.textAlign = 'center'
                        ctx.textBaseline = 'middle'

                        // Draw node circle background
                        const nodeColor = getNodeColor(node.type, node.status)
                        const isHovered = hoveredNode === node.id
                        const radius = isHovered ? 18 : 15

                        // Background circle with slight transparency
                        ctx.fillStyle = nodeColor
                        ctx.globalAlpha = 0.15
                        ctx.beginPath()
                        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI)
                        ctx.fill()
                        ctx.globalAlpha = 1

                        // Border circle
                        ctx.strokeStyle = nodeColor
                        ctx.lineWidth = isHovered ? 3 : 2
                        ctx.beginPath()
                        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI)
                        ctx.stroke()

                        // Draw icon in the center based on node type/label
                        const iconType = getNodeIcon(node.label, node.type)
                        ctx.fillStyle = nodeColor
                        ctx.strokeStyle = nodeColor
                        ctx.lineWidth = 2

                        // Draw icon representation
                        if (iconType === Bot) {
                            // Assistant icon - robot head with eyes
                            ctx.fillStyle = nodeColor
                            ctx.beginPath()
                            ctx.arc(node.x, node.y - 1, 5, 0, 2 * Math.PI) // Head
                            ctx.fill()
                            ctx.fillStyle = 'white'
                            ctx.beginPath()
                            ctx.arc(node.x - 2.5, node.y - 1, 1.2, 0, 2 * Math.PI) // Left eye
                            ctx.arc(node.x + 2.5, node.y - 1, 1.2, 0, 2 * Math.PI) // Right eye
                            ctx.fill()
                        } else if (iconType === User) {
                            // User icon - person silhouette
                            ctx.fillStyle = nodeColor
                            ctx.beginPath()
                            ctx.arc(node.x, node.y - 3, 4, 0, 2 * Math.PI) // Head
                            ctx.fill()
                            ctx.beginPath()
                            ctx.moveTo(node.x, node.y + 1)
                            ctx.lineTo(node.x - 3, node.y + 6)
                            ctx.lineTo(node.x + 3, node.y + 6)
                            ctx.closePath()
                            ctx.fill()
                        } else if (iconType === MessageSquare) {
                            // Teams icon - chat bubble
                            ctx.fillStyle = nodeColor
                            ctx.fillRect(node.x - 6, node.y - 5, 12, 9)
                            ctx.strokeStyle = nodeColor
                            ctx.lineWidth = 1.5
                            ctx.strokeRect(node.x - 6, node.y - 5, 12, 9)
                            // Small tail
                            ctx.beginPath()
                            ctx.moveTo(node.x + 3, node.y + 4)
                            ctx.lineTo(node.x + 7, node.y + 7)
                            ctx.lineTo(node.x + 5, node.y + 4)
                            ctx.closePath()
                            ctx.fill()
                        } else if (iconType === Mail) {
                            // Email/Outlook icon - envelope
                            ctx.strokeStyle = nodeColor
                            ctx.lineWidth = 2
                            ctx.strokeRect(node.x - 6, node.y - 3, 12, 8)
                            ctx.beginPath()
                            ctx.moveTo(node.x - 6, node.y - 3)
                            ctx.lineTo(node.x, node.y + 2)
                            ctx.lineTo(node.x + 6, node.y - 3)
                            ctx.stroke()
                        } else if (iconType === Folder) {
                            // Folder icon for SharePoint/OneDrive
                            ctx.fillStyle = nodeColor
                            ctx.beginPath()
                            ctx.moveTo(node.x - 6, node.y - 1)
                            ctx.lineTo(node.x - 4, node.y - 4)
                            ctx.lineTo(node.x + 6, node.y - 4)
                            ctx.lineTo(node.x + 6, node.y + 5)
                            ctx.lineTo(node.x - 6, node.y + 5)
                            ctx.closePath()
                            ctx.fill()
                            ctx.strokeStyle = nodeColor
                            ctx.lineWidth = 1.5
                            ctx.stroke()
                        }

                        // Draw label below node
                        ctx.fillStyle = 'hsl(var(--foreground))'
                        ctx.fillText(label, node.x, node.y + radius + 18)

                        // Draw status badge if exists
                        if (node.status && node.status !== 'connected') {
                            ctx.fillStyle = 'hsl(var(--background))'
                            ctx.beginPath()
                            ctx.arc(node.x + radius - 4, node.y - radius + 4, 6, 0, 2 * Math.PI)
                            ctx.fill()

                            ctx.strokeStyle = getNodeColor(node.type, node.status)
                            ctx.lineWidth = 1.5
                            ctx.stroke()
                        }
                    }}
                    linkCanvasObject={(link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
                        // Draw link with custom styling
                        const color = getEdgeColor(link.type)
                        const isDashed = link.type === 'approval' || link.type === 'blocked'

                        ctx.strokeStyle = color
                        ctx.lineWidth = link.type === 'blocked' ? 1 : 2
                        ctx.globalAlpha = link.type === 'blocked' ? 0.5 : 1

                        if (isDashed) {
                            ctx.setLineDash([5, 5])
                        } else {
                            ctx.setLineDash([])
                        }

                        ctx.beginPath()
                        ctx.moveTo(link.source.x, link.source.y)
                        ctx.lineTo(link.target.x, link.target.y)
                        ctx.stroke()

                        ctx.setLineDash([])
                        ctx.globalAlpha = 1

                        // Draw icon in the middle for approval/blocked
                        if (link.type === 'approval' || link.type === 'blocked') {
                            const midX = (link.source.x + link.target.x) / 2
                            const midY = (link.source.y + link.target.y) / 2

                            ctx.fillStyle = 'hsl(var(--background))'
                            ctx.beginPath()
                            ctx.arc(midX, midY, 8, 0, 2 * Math.PI)
                            ctx.fill()

                            ctx.strokeStyle = color
                            ctx.lineWidth = 1
                            ctx.stroke()

                            if (link.type === 'approval') {
                                // Draw lock icon
                                ctx.strokeStyle = color
                                ctx.lineWidth = 1.5
                                ctx.beginPath()
                                // Lock body
                                ctx.rect(midX - 4, midY + 1, 8, 5)
                                ctx.stroke()
                                // Lock shackle
                                ctx.beginPath()
                                ctx.arc(midX, midY + 1, 3, Math.PI, 0, false)
                                ctx.stroke()
                            } else if (link.type === 'blocked') {
                                // Draw X icon
                                ctx.strokeStyle = color
                                ctx.lineWidth = 2
                                ctx.beginPath()
                                ctx.moveTo(midX - 4, midY - 4)
                                ctx.lineTo(midX + 4, midY + 4)
                                ctx.moveTo(midX + 4, midY - 4)
                                ctx.lineTo(midX - 4, midY + 4)
                                ctx.stroke()
                            }
                        }
                    }}
                    cooldownTicks={150}
                    onEngineStop={() => {
                        // Graph has finished calculating positions
                    }}
                    enableZoomInteraction={true}
                    enablePanInteraction={true}
                />
            </div>

            {/* Selected Node Details Panel */}
            {selectedNode && (
                <div className="mt-4 pt-4 border-t">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-sm">{selectedNode.label}</h4>
                            <button
                                onClick={() => setSelectedNode(null)}
                                className="text-muted-foreground hover:text-foreground"
                                aria-label="Close details"
                            >
                                <XCircle className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                                {selectedNode.type}
                            </Badge>
                            {selectedNode.status && (
                                <Badge
                                    variant={
                                        selectedNode.status === 'connected' ? 'default' :
                                            selectedNode.status === 'approval' ? 'secondary' :
                                                selectedNode.status === 'blocked' ? 'destructive' : 'outline'
                                    }
                                    className="text-xs"
                                >
                                    {selectedNode.status}
                                </Badge>
                            )}
                        </div>
                        {selectedNode.description && (
                            <p className="text-sm text-muted-foreground">
                                {selectedNode.description}
                            </p>
                        )}
                        {!selectedNode.description && (
                            <p className="text-sm text-muted-foreground">
                                {selectedNode.type === 'assistant' && 'Your personal digital assistant that helps with tasks and queries.'}
                                {selectedNode.type === 'source' && 'Connected data source that provides information to your assistant.'}
                                {selectedNode.type === 'agent' && 'AI agent that processes requests and provides responses.'}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
